import React, { useCallback, useMemo } from "react";
import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ResponseObject } from "../../classes/main";
import SearchServices from "../search-services/SearchServices";
import { ApiService } from "@/app/services/AppService";
import { getHubId } from "../genericComponents/helper-functions/currencyCode";
import { SEARCH } from "../search-services/searchUi";

interface SearchBarProps {
  setReloadCartService: any;
}

const QUICK_SEARCHES = ["Facial", "Waxing", "Manicure", "Hair spa"];

export const SearchBar: React.FC<SearchBarProps> = ({ setReloadCartService }) => {
  const [openSearchDialog, setOpenSearchDialog] = useState(false);
  const [optionList, setOptionList] = useState<string[]>([]);
  const [searchServiceList, setSearchServiceList] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [serviceName, setServiceName] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const apiService = useMemo(() => new ApiService(), []);
  const xHubId = getHubId();

  const uniqueKeyOptions = optionList?.map((item, index) => ({
    id: index,
    label: item,
  }));

  const handleSelectedSearchService = (name: string) => {
    if (!name?.trim()) return;
    setOpenSearchDialog(true);
    setServiceName(name.trim());
  };

  const getSearchServicesWithName = useCallback(async () => {
    if (!serviceName) {
      setSearchServiceList([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    setSearchServiceList([]);
    try {
      const responseObject: ResponseObject | undefined =
        await apiService.get<ResponseObject>(
          apiService.searchServicesWithName + serviceName,
          undefined,
          xHubId
        );
      const response = responseObject?.object;
      setSearchServiceList(Array.isArray(response) ? response : []);
    } catch {
      setSearchServiceList([]);
    } finally {
      setSearchLoading(false);
    }
  }, [apiService, serviceName, xHubId]);

  useEffect(() => {
    getSearchServicesWithName();
  }, [serviceName, getSearchServicesWithName]);

  const getSearchServices = useCallback(async () => {
    if (!inputValue?.trim()) {
      setOptionList([]);
      return;
    }
    try {
      const responseObject: ResponseObject | undefined =
        await apiService.get<ResponseObject>(
          apiService.searchServicesKeywords + inputValue
        );
      const response = responseObject?.object;
      setOptionList(Array.isArray(response) ? response : []);
    } catch {
      setOptionList([]);
    }
  }, [apiService, inputValue]);

  useEffect(() => {
    getSearchServices();
  }, [inputValue, getSearchServices]);

  return (
    <>
      <Box
        sx={{
          display: { xs: "none", sm: "block" },
          width: "100%",
          maxWidth: { sm: 440, md: 500, lg: 540 },
        }}
      >
        <Autocomplete
          freeSolo
          id="salontym-navbar-search"
          options={uniqueKeyOptions}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option?.label ?? ""
          }
          onInputChange={(event, newInputValue, reason) => {
            if (reason === "clear") {
              setInputValue("");
              setOptionList([]);
            } else {
              setInputValue(newInputValue.toLowerCase());
            }
          }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                borderRadius: "16px",
                border: `1px solid ${SEARCH.border}`,
                boxShadow: "0 20px 50px rgba(16, 33, 23, 0.12)",
                overflow: "hidden",
              },
            },
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              borderRadius: "999px",
              bgcolor: SEARCH.surfaceElevated,
              pl: 0.5,
              transition: "box-shadow 0.2s ease, border-color 0.2s ease",
              "&:hover": {
                bgcolor: "#fff",
                boxShadow: `0 4px 20px ${alpha(SEARCH.primary, 0.08)}`,
              },
              "&.Mui-focused": {
                bgcolor: "#fff",
                boxShadow: `0 4px 24px ${alpha(SEARCH.primary, 0.14)}`,
              },
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(SEARCH.primary, 0.22),
            },
            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: `${SEARCH.primary} !important`,
              borderWidth: "1px !important",
            },
          }}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              key={option?.id}
              onClick={() => handleSelectedSearchService(String(option?.label))}
              sx={{
                py: 1.25,
                px: 1.5,
                gap: 1,
                display: "flex",
                alignItems: "center",
                "&:hover": { bgcolor: alpha(SEARCH.primary, 0.06) },
              }}
            >
              <SearchIcon sx={{ fontSize: 18, color: alpha(SEARCH.primary, 0.55) }} />
              <Typography sx={{ fontWeight: 600, color: SEARCH.text, fontSize: "0.95rem" }}>
                {option?.label}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search facials, waxing, spa…"
              aria-label="Search salon services"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchIcon
                      sx={{
                        ml: 0.75,
                        mr: 0.25,
                        color: SEARCH.primary,
                        fontSize: 22,
                      }}
                    />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              inputProps={{
                ...params.inputProps,
                style: { paddingLeft: 4 },
              }}
            />
          )}
        />
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.75,
            mt: 1,
            pl: 0.5,
          }}
        >
          <Typography
            component="span"
            sx={{ fontSize: "0.7rem", fontWeight: 700, color: SEARCH.muted, mr: 0.5 }}
          >
            Quick:
          </Typography>
          {QUICK_SEARCHES.map((q) => (
            <Chip
              key={q}
              label={q}
              size="small"
              onClick={() => handleSelectedSearchService(q)}
              sx={{
                height: 26,
                fontSize: "0.72rem",
                fontWeight: 700,
                bgcolor: alpha(SEARCH.primary, 0.08),
                color: SEARCH.primaryDark,
                border: `1px solid ${alpha(SEARCH.primary, 0.15)}`,
                "&:hover": {
                  bgcolor: alpha(SEARCH.primary, 0.14),
                },
              }}
            />
          ))}
        </Box>
      </Box>
      <SearchServices
        openSearchDialog={openSearchDialog}
        handleCloseSearchDialog={() => setOpenSearchDialog(false)}
        searchServiceList={searchServiceList}
        setSearchServiceList={setSearchServiceList}
        setServiceName={setServiceName}
        setReloadCartService={setReloadCartService}
        searchQuery={serviceName}
        isLoading={searchLoading}
      />
    </>
  );
};
